// ─────────────────────────────────────────────
//  roomCampusRules — WHICH HOSPITAL a room label belongs to, as data.
//
//  ⚠️ THE CAMPUS DISCRIMINATOR IS ONE SPACE, AND IT IS COUNTER-INTUITIVE.
//
//      WC5   → TAMPA          (Women's Center suite 5)
//      WC 5  → WESLEY CHAPEL  (…and Wesley Chapel has no room "5")
//
//  Everybody reads `^wc\d` backwards the first time, and it has caused an
//  incident. So the rule is not a regex here — it is a TABLE of rules, each
//  carrying WHY it is true and the EVIDENCE that proved it, and the regexes
//  the importer filters rows with are DERIVED from that table. Change the
//  table and every consumer changes with it.
//
//  Decisions are made in roomCampus.js. The department's published room list
//  is read by features/sheet-import/roomDefs.js and partitioned by campus in
//  roomDefsCampus.js. Pure — no Firebase, no React, no network.
//
//  Anesthesia Team Control (ATC)
//  Copyright © 2026 Gonzalo Martinez (gamc00@gmail.com). All rights reserved.
//  Proprietary and confidential. Not licensed for public use — see /LICENSE.
// ─────────────────────────────────────────────

// ──────────────────────────────────────
//  CAMPUS_RULES — the shipped rule set, expressed as data.
//
//  Order is REPORTING order only; `campusForRoomLabel` evaluates every rule
//  so that a label claimed by two of them is reported as contested rather
//  than resolved by whichever happened to be written first.
//
//  `pattern` is anchored at the start of the label, matching the shipped
//  behaviour exactly. `roomFamilyPattern()` recomposes these into the
//  per-facility RegExp `parsePrelimAssignments` filters rows with, so the
//  table and the importer can never drift apart.
// ──────────────────────────────────────
export const CAMPUS_RULES = [
  {
    id: "wesley-chapel-or",
    facilityId: "default",
    pattern: /^wor(?=\d|\s|$)/i,
    shape: "WOR<n> / WOR <n>",
    why:
      "Wesley Chapel's main operating rooms. The lookahead for a digit, a space " +
      "or end-of-string exists so that a word merely beginning with those three " +
      'letters — "WORLD" — is not read as an operating room.',
    evidence: "Room Defs 2026-08-20: WOR 1…WOR 12 → Epic WCH S01…WCH S12.",
  },
  {
    id: "wesley-chapel-area",
    facilityId: "default",
    pattern: /^wc(?=\s)/i,
    shape: "WC <label> — WITH A SPACE",
    why:
      "Wesley Chapel's non-OR anaesthetising areas are written as the campus " +
      "initials, a space, then the area: WC OB, WC CS, WC E1-3, WC TEE, WC CL1/2, " +
      "WC IR, WC CT, WC MRI. The SPACE is the entire difference from the Tampa " +
      "rule below.",
    evidence:
      "Room Defs 2026-08-20: WC IR → AHWCH IR 1, WC CT → AHWCH CT 1, " +
      "WC MRI → AHWCH MR 1 (3T), WC CS → WCH OB01. Every Epic name carries AHWCH/WCH.",
    counterIntuitive: true,
  },
  {
    id: "tampa-womens-center",
    facilityId: "tampa",
    pattern: /^wc\d/i,
    shape: "WC<digit> — NO SPACE",
    why:
      "⚠️ READ THIS TWICE. WC1…WC7 are TAMPA's Women's Center suites, NOT Wesley " +
      "Chapel. They sit on the Tampa OR board, in column M, under a section header " +
      'that also reads "WC". The initials collide; the space is the discriminator.',
    evidence:
      "Room Defs 2026-08-20: WC1…WC7 → Epic TAM WC S01…TAM WC S07 (TAM = Tampa). " +
      "Corroborated by the AHT OR Board (EVEN/ODD) column M, rows 19-26, and by " +
      "atc-app-latest.csv carrying WC1/WC2/WC5/WC6/WC7 alongside WC MRI/WC E1/WC OB " +
      "in the SAME export.",
    counterIntuitive: true,
  },
  {
    id: "tampa-main-or",
    facilityId: "tampa",
    pattern: /^tor/i,
    shape: "TOR<n> / TOR <n>",
    why:
      "Tampa's main operating rooms. Each campus prefixes its OR numbering " +
      "differently — TOR at Tampa, COR at Carrollwood, WOR at Wesley Chapel — " +
      'so a bare number like "6" belongs to whichever board it was written on, ' +
      "and the three never collide.",
    evidence: "Room Defs 2026-08-20: TOR 1…TOR 18 → Epic TAM TOW S01…TAM TOW S18.",
  },
  {
    id: "tampa-areas",
    facilityId: "tampa",
    // Byte-for-byte the alternation shipped in ROOM_FAMILY_BY_FACILITY.tampa,
    // minus the two rules given their own entries above (tor, wc\d) and minus
    // the bench token (tbd), which is campus-neutral and handled separately.
    pattern: /^(ct\b|endo|cie|cv|ni\b|ep|cl|ir\b|mri\b|flex\b|ob\b|acutecare)/i,
    shape: "the bare area word — CT, ENDO n, CIE n, CV n, NI, EP n, CL n, IR, MRI, FLEX, OB, ACUTECARE",
    why:
      "⚠️ TAMPA OWNS THE UNPREFIXED WORD. Tampa's procedural areas are typed with " +
      "no campus prefix at all, so the generic word means Tampa — and that is " +
      "correct, per the published list. It is also the open door: the same generic " +
      "words are printed as SECTION HEADERS on the Carrollwood and Wesley Chapel " +
      "tabs, so a typist working in the local dialect can send a case to Tampa " +
      "without a single error anywhere. See CROSS_CAMPUS_AREAS.",
    evidence:
      "Room Defs 2026-08-20: IR → AHTAM IR 1; CT → AHTAM CT DX 1; MRI → AHTAM MR 1 " +
      "(1.5T); ENDO1 → AHTAM E01; CIE5 → AHTAM CIE E05; CV1 → TAM CV01; " +
      "NI → AHTAM CV EC - TEE; EP1 → EP LAB 1; CL7 → AHTAM CV CATH/NEURO LAB 7.",
  },
  {
    id: "carrollwood",
    facilityId: "carrollwood",
    pattern: /^(cor|car)/i,
    shape: "COR<n> (operating rooms) / CAR <label> (areas)",
    why:
      "Carrollwood's main ORs are COR n; its areas are CAR <label>. Marked " +
      "provisional/UNVERIFIED in this codebase since it was first transcribed " +
      "from a comment — that is no longer true, see the evidence.",
    evidence:
      "Room Defs 2026-08-20 — VERIFIED: COR 1…COR 12 → Epic CAR S01…CAR S12; " +
      "CAR CT → AHCAR CT 1; CAR MRI → AHCAR MR 1 (1.5T); CAR E1 → AHCAR E01; " +
      "CAR TEE → AHCAR CV EC 1.",
  },
];

// ──────────────────────────────────────
//  The bench token. `TBD` is not a room — a row landing there parks its people
//  on the unassigned bench (see isTbdRoomName / the bench path in
//  toSheetDraftGrid), so as an ANSWER it is campus-neutral and never competes
//  with a campus rule.
//
//  ⚠️ AS A ROW FILTER IT IS NOT NEUTRAL, AND GETTING THAT WRONG DUPLICATES
//  PEOPLE. The sheet writes the bench three ways — `TBD`, `CAR TBD`, `WC TBD` —
//  and the two prefixed forms are already claimed by their own campus rules.
//  The BARE form appears only in the Tampa block. Adding `tbd` to all three
//  families made one bare `TBD` row match all three, and two real CRNAs
//  (Thomas Klaphake, Dave Cadell) were copied onto three benches from a single
//  row. It belongs to exactly one family, exactly as shipped.
//
//  `globalSheet2026-07-07.regression.test.js` guards this: it asserts Wesley
//  Chapel parses ZERO rows out of an all-Tampa sheet, the "…and not Wesley
//  Chapel" half of the `WC1 = Cassie Samuels` Women's-Center assertion.
// ──────────────────────────────────────
export const BENCH_PATTERN = /^tbd\b/i;
export const BENCH_FAMILY_OWNER = "tampa";

// ──────────────────────────────────────
//  SUPPLEMENTAL_ROOMS — real, staffed anaesthetising locations that the
//  department's own `Room Defs` tab does not list.
//
//  ⚠️ THE CONTRACT SAID `Room Defs` CONTAINED THESE. IT DOES NOT. Fetched
//  2026-08-20, the tab holds 96 distinct Prelim Room Names and BABY, ICU and
//  ACUTECARE are none of them. `ACUTECARE` survives today only because the
//  Tampa area alternation happens to name it; `BABY` and `ICU` match no
//  pattern in the system and are therefore invisible — not skipped, not
//  logged, not counted. A person standing in one of them does not exist.
//
//  Listing them here routes the label to a campus so the row reaches the
//  resolver, where a missing room doc becomes a NAMED review item instead of
//  a silence.
// ──────────────────────────────────────
export const SUPPLEMENTAL_ROOMS = [
  {
    label: "BABY",
    facilityId: "default",
    why: "Wesley Chapel's nursery/neonatal location, staffed like a room.",
    evidence:
      "AHWC Board (EVEN) and (ODD), column A, below WC CS. Absent from Room Defs 2026-08-20.",
  },
  {
    label: "ICU",
    facilityId: "tampa",
    why: "Tampa ICU — anaesthesia is scheduled into it like any other location.",
    evidence: "AHT OR Board (EVEN) and (ODD), column A. Absent from Room Defs 2026-08-20.",
  },
  {
    label: "ACUTECARE",
    facilityId: "tampa",
    why:
      "Tampa acute-care coverage. Already claimed by the Tampa area pattern; " +
      "repeated here so the fact that the published list omits it is recorded " +
      "where somebody will read it.",
    evidence:
      "Named in parsePrelimAssignments' column-C rescue and carried a real person " +
      "(Sarabel Mercado Torres) on the 2026-08-20 sheet. Absent from Room Defs.",
  },
];

// ──────────────────────────────────────
//  ENDO_ALIAS_FOLD — the ONE synonym this file admits, and it is quoted from
//  the department, not invented.
//
//  Tampa writes its endoscopy suites ENDO1/ENDO2/ENDO3; Carrollwood and Wesley
//  Chapel write theirs CAR E1/WC E1. The Epic column proves they are the same
//  kind of room named three ways: AHTAM E01 / AHCAR E01 / AHWCH E01.
//
//  ⚠️ USED FOR FLAGGING ONLY. It never resolves a label to a room — folding
//  ENDO1 into E1 for matching would let one campus's endoscopy suite answer
//  for another's, which is the exact defect this module exists to close.
// ──────────────────────────────────────
export const ENDO_ALIAS_FOLD = {
  pattern: /^endo\s*(\d+)$/i,
  to: (m) => `E${Number(m[1])}`,
  evidence: "Room Defs 2026-08-20 Epic column: AHTAM E01 = AHCAR E01 = AHWCH E01.",
};

// The campus prefixes that make a label unambiguous, and the OR prefixes that
// are per-campus by construction (TOR/COR/WOR each belong to exactly one
// hospital, so "TOR 5" and "WOR 5" are not two campuses claiming "5").
const AREA_PREFIXES = [
  { prefix: /^wc\s+/i, facilityId: "default" },
  { prefix: /^car\s+/i, facilityId: "carrollwood" },
];
const OR_PREFIX_BY_FACILITY = { tampa: "TOR ", carrollwood: "COR ", default: "WOR " };
const OR_PREFIX_RE = /^(tor|cor|wor)\s*\d/i;

/** Squash to a comparison key: upper case, no whitespace, no punctuation. */
export const areaKey = (s) =>
  String(s ?? "")
    .toUpperCase()
    .replace(/[\s\-_./]+/g, "");

/** Split a canonical room name into { facilityId, area } for risk indexing. */
export function splitArea(name) {
  const raw = String(name ?? "").trim();
  if (!raw) return null;
  for (const { prefix, facilityId } of AREA_PREFIXES) {
    if (prefix.test(raw)) return { facilityId, area: raw.replace(prefix, "") };
  }
  if (OR_PREFIX_RE.test(raw)) return null; // per-campus OR numbering — never shared
  if (/^wc\d/i.test(raw)) return null; // Tampa Women's Center — its own numbering
  return { facilityId: "tampa", area: raw }; // the bare Tampa dialect
}

export function foldArea(area) {
  const m = ENDO_ALIAS_FOLD.pattern.exec(String(area ?? "").trim());
  return m ? ENDO_ALIAS_FOLD.to(m) : area;
}

// ──────────────────────────────────────
//  roomFamilyPattern
//
//  The per-facility RegExp `parsePrelimAssignments` filters sheet rows with,
//  RECOMPOSED FROM CAMPUS_RULES so that the table above is the single
//  definition of the campus split.
//
//  @param {string} facilityId
//  @param {object} [opts]
//    @param {boolean} [opts.supplemental=true] include SUPPLEMENTAL_ROOMS
//      (BABY / ICU / ACUTECARE). Pass false for the byte-exact shipped
//      behaviour — used by the regression test that pins the two against each
//      other over the whole observed vocabulary.
//  @returns {RegExp}
// ──────────────────────────────────────
export function roomFamilyPattern(facilityId, opts = {}) {
  const supplemental = opts.supplemental !== false;
  const parts = CAMPUS_RULES.filter((r) => r.facilityId === facilityId).map((r) =>
    r.pattern.source.replace(/^\^/, "")
  );
  if (!parts.length) return /(?!)/;
  // ⚠️ ONE family, not three. See BENCH_FAMILY_OWNER.
  if (facilityId === BENCH_FAMILY_OWNER) parts.push(BENCH_PATTERN.source.replace(/^\^/, ""));
  if (supplemental) {
    SUPPLEMENTAL_ROOMS.filter((s) => s.facilityId === facilityId).forEach((s) => {
      // ACUTECARE is already inside the Tampa area alternation. Repeating it
      // would only make the pattern harder to read.
      const alt = `${s.label.replace(/[.*+?^${}()|[\]\\]/g, "\\$&")}\\b`;
      if (!parts.includes(alt) && !new RegExp(`^(${parts.join("|")})`, "i").test(s.label)) {
        parts.push(alt);
      }
    });
  }
  return new RegExp(`^(${parts.join("|")})`, "i");
}

/**
 * ⚠️ NO LABEL MAY BELONG TO TWO HOSPITALS. Returns every label in `labels`
 * claimed by more than one facility's row filter — which is how one sheet row
 * becomes the same person standing on two boards. Kept as a callable so the
 * test suite can run it over the whole observed vocabulary on every change.
 */
export function multiClaimedLabels(labels, facilityIds = CAMPUS_FACILITY_IDS) {
  const pats = facilityIds.map((f) => [f, roomFamilyPattern(f)]);
  const out = [];
  (Array.isArray(labels) ? labels : []).forEach((label) => {
    const hits = pats.filter(([, re]) => re.test(String(label ?? ""))).map(([f]) => f);
    if (hits.length > 1) out.push({ label, facilityIds: hits });
  });
  return out;
}

/** Every facility this table knows a rule for. */
export const CAMPUS_FACILITY_IDS = [...new Set(CAMPUS_RULES.map((r) => r.facilityId))];

// ──────────────────────────────────────
//  campusPrefixFor / qualifyHint
//
//  The sheet's routing arrows are written in the LOCAL dialect: a Wesley Chapel
//  row says "to TEE" meaning WC TEE; a Carrollwood row says "to OR 6" meaning
//  COR 6. `qualifyHint` re-expresses such a hint in the campus of the row it
//  was written on, so the arrow can be matched against real room docs.
//
//  Returns candidate labels in decreasing confidence, ALWAYS starting with the
//  hint exactly as written — a hint that already names a real room must never
//  be rewritten.
// ──────────────────────────────────────
export function campusPrefixFor(facilityId) {
  const area = AREA_PREFIXES.find((p) => p.facilityId === facilityId);
  return {
    or: OR_PREFIX_BY_FACILITY[facilityId] || "",
    area: area ? (facilityId === "carrollwood" ? "CAR " : "WC ") : "",
  };
}
